import type { FeedbackCategory } from '@/lib/feedback/types';

// Admin sub-roles
export type AdminSubRole = 'super_admin' | 'support' | 'analyst' | 'moderator';

// Database row shape for admin_roles table
export interface AdminRole {
  id: string;
  user_id: string;
  admin_role: AdminSubRole;
  granted_by: string | null;
  created_at: string;
}

// Admin screen permission keys (extended)
export type AdminPermission =
  | 'dashboard'
  | 'dashboard_engagement'
  | 'dashboard_activity_log'
  | 'feedback'
  | 'feedback_manage'
  | 'feedback_notes'
  | 'users'
  | 'users_detail'
  | 'users_analytics';

// Permission matrix: which sub-roles can access which features
export const ADMIN_PERMISSIONS: Record<AdminSubRole, AdminPermission[]> = {
  super_admin: [
    'dashboard', 'dashboard_engagement', 'dashboard_activity_log',
    'feedback', 'feedback_manage', 'feedback_notes',
    'users', 'users_detail', 'users_analytics',
  ],
  support: [
    'dashboard',
    'feedback', 'feedback_manage', 'feedback_notes',
    'users', 'users_detail',
  ],
  analyst: [
    'dashboard', 'dashboard_engagement',
    'users', 'users_detail', 'users_analytics',
  ],
  moderator: [
    'dashboard',
    'users', 'users_detail',
  ],
};

// Check if a sub-role has a specific permission
export function hasPermission(role: AdminSubRole | null | undefined, permission: AdminPermission): boolean {
  if (!role) return false;
  return ADMIN_PERMISSIONS[role]?.includes(permission) ?? false;
}

// ---- Feedback types ----

export type FeedbackStatus = 'new' | 'in_review' | 'resolved' | 'closed';
export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

// Feedback item with user email join (for admin feedback list) — extended
export interface AdminFeedbackItem {
  id: string;
  user_id: string;
  user_email: string;
  platform: string;
  category: FeedbackCategory;
  text: string;
  screenshot_path: string | null;
  screen_context: string | null;
  app_version: string | null;
  device_info: Record<string, unknown>;
  created_at: string;
  // Management fields
  status: FeedbackStatus;
  priority: FeedbackPriority;
  assigned_to: string | null;
  resolved_at: string | null;
  updated_at: string;
}

// Admin note on a feedback item
export interface AdminFeedbackNote {
  id: string;
  feedback_id: string;
  admin_email: string;
  text: string;
  created_at: string;
}

// Full feedback detail with notes
export interface FeedbackDetailData {
  feedback: AdminFeedbackItem;
  notes: AdminFeedbackNote[];
}

// ---- Activity log ----

export interface AdminActivityLogEntry {
  id: string;
  admin_email: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

// ---- User detail ----

export interface AdminUserDetail {
  id: string;
  email: string;
  role: 'startup' | 'investor' | 'admin' | 'pending';
  onboarding_completed: boolean;
  created_at: string;
  last_active: string | null;
  login_count: number;
  feedback_count: number;
  message_count: number;
  score: number | null;
  data_room_files: number;
  data_room_views: number;
}

export interface UserActivityEvent {
  id: string;
  event_type: 'login' | 'feedback' | 'message' | 'upload' | 'score_update' | 'onboarding' | 'data_room_view';
  description: string;
  metadata: Record<string, unknown>;
  timestamp: string;
}

export interface UserActivitySummary {
  activityByDay: { date: string; count: number }[];
  recentEvents: UserActivityEvent[];
}

// ---- Dashboard metrics V2 ----

export interface AdminDashboardMetricsV2 {
  // Base counts
  totalUsers: number;
  totalStartups: number;
  totalInvestors: number;
  totalAdmins: number;
  totalFeedback: number;
  feedbackByCategory: Record<FeedbackCategory, number>;
  recentSignups7d: number;
  // Growth deltas (percentage change vs prior period)
  usersDelta: number;
  startupsDelta: number;
  investorsDelta: number;
  feedbackDelta: number;
  // Engagement
  dau: number;
  wau: number;
  mau: number;
  // Feedback management
  openFeedbackCount: number;
  criticalBugs: number;
  feedbackByStatus: Record<FeedbackStatus, number>;
  // Scores & activity
  avgScore: number;
  messageVolume: number;
  dataRoomActivity: number;
  // Onboarding
  pendingOnboarding: number;
  // Signup trend (last 7 days)
  signupTrend: { date: string; count: number }[];
}

// ---- Legacy compat ----

// Dashboard metrics aggregate (kept for backward compat)
export interface AdminDashboardMetrics {
  totalUsers: number;
  totalStartups: number;
  totalInvestors: number;
  totalAdmins: number;
  totalFeedback: number;
  feedbackByCategory: Record<FeedbackCategory, number>;
  recentSignups7d: number;
}

// User profile row for admin user listing
export interface AdminUserItem {
  id: string;
  email: string;
  role: 'startup' | 'investor' | 'admin';
  onboarding_completed: boolean;
  created_at: string;
}

// Sub-role display labels
export const ADMIN_ROLE_LABELS: Record<AdminSubRole, string> = {
  super_admin: 'Super Admin',
  support: 'Support',
  analyst: 'Analyst',
  moderator: 'Moderator',
};
